/**
 * ENFAMI Premium — orchestrateur global (Lenis, GSAP, interactions)
 */
(function () {
  'use strict';

  const MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  const REDUCED_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  const isMobile = () => MOBILE_MQ.matches;
  const isReduced = () => REDUCED_MQ.matches;

  const html = document.documentElement;
  const body = document.body;

  html.classList.add('enfami-premium');
  body.classList.add('enfami-premium');

  if (isMobile()) body.classList.add('enfami-premium--touch');
  if (isReduced()) body.classList.add('enfami-reduced-motion');

  MOBILE_MQ.addEventListener('change', (e) => {
    body.classList.toggle('enfami-premium--touch', e.matches);
  });

  /* ── Loader — navigation directe, sans écran de chargement ── */
  function initLoader() {
    const loader = document.querySelector('[data-loader]');
    const blocker = document.getElementById('view-transition-render-blocker');

    if (blocker) blocker.remove();

    if (!loader) {
      document.dispatchEvent(new CustomEvent('enfami:premium-ready'));
      return;
    }

    loader.classList.add('is-hidden');
    document.dispatchEvent(new CustomEvent('enfami:premium-ready'));
  }

  /* ── Scroll progress ── */
  function initScrollProgress() {
    const bar = document.getElementById('enfami-scroll-progress');
    if (!bar) return;

    const update = (scroll) => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (scroll / max) * 100 : 0;
      bar.style.setProperty('--scroll-progress', pct + '%');
    };

    document.addEventListener('enfami:scroll', (e) => update(e.detail.scroll));
    window.addEventListener('scroll', () => update(window.scrollY), { passive: true });
    update(window.scrollY);
  }

  /* ── Header glass ── */
  function initHeaderGlass() {
    const threshold = 48;
    const toggle = (y) => body.classList.toggle('header-glass', y > threshold);
    toggle(window.scrollY);
    document.addEventListener('enfami:scroll', (e) => toggle(e.detail.scroll));
    window.addEventListener('scroll', () => toggle(window.scrollY), { passive: true });
  }

  /* ── Custom cursor ── */
  function initCursor() {
    /* Curseur natif — custom désactivé */
  }

  /* ── Magnetic buttons ── */
  function initMagnetic() {
    if (isMobile() || isReduced()) return;

    document.querySelectorAll('.enfami-magnetic').forEach((btn) => {
      const inner = btn.querySelector('.enfami-magnetic__inner') || btn;
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        inner.style.transform = `translate(${x * 0.18}px, ${y * 0.18}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        inner.style.transform = '';
      });
    });
  }

  /* ── 3D tilt cartes ── */
  function bindTilt(card) {
    if (card.dataset.tiltBound) return;
    card.dataset.tiltBound = 'true';

    const inner = card.querySelector('.enfami-carousel__img-wrap, .vc-thumb, .product-card-gallery, .card-gallery') || card;
    inner.style.transformStyle = 'preserve-3d';
    inner.style.transition = 'transform 0.2s var(--ep-ease, ease)';

    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      inner.style.transform = `perspective(800px) rotateY(${x * 4}deg) rotateX(${-y * 4}deg) scale(1.02)`;
    });
    card.addEventListener('mouseleave', () => {
      inner.style.transform = '';
    });
  }

  function initTilt() {
    if (isMobile() || isReduced()) return;

    document.querySelectorAll('[data-tilt], product-card, .product-card').forEach(bindTilt);
  }

  /* ── Compteurs animés ── */
  function initCounters() {
    const els = document.querySelectorAll('[data-count-to]');
    if (!els.length) return;

    const animate = (el) => {
      const target = parseInt(el.getAttribute('data-count-to') || '0', 10);
      const suffix = el.getAttribute('data-count-suffix') || '';
      const duration = 1800;
      const start = performance.now();

      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = Math.round(target * eased) + suffix;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animate(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    els.forEach((el) => io.observe(el));
  }

  /* ── Galerie PDP : visible immédiatement (live + dev) ── */
  function initProductGalleryFix() {
    if (!document.body.classList.contains('template-product')) return;

    document.querySelectorAll('.shopify-section:has(.product-information), .product-information').forEach((el) => {
      el.classList.add('is-revealed');
      el.classList.remove('is-pending-reveal');
      el.removeAttribute('data-reveal');
      el.style.setProperty('opacity', '1', 'important');
      el.style.setProperty('transform', 'none', 'important');
      el.style.setProperty('visibility', 'visible', 'important');
    });

    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    gallery.classList.remove('media-gallery--large-first-image');

    const isWide = window.matchMedia('(min-width: 990px)').matches;
    const isTablet = window.matchMedia('(min-width: 750px)').matches;
    const slideshow = gallery.querySelector('slideshow-component');
    const grid = gallery.querySelector('.media-gallery__grid');
    const cells = gallery.querySelectorAll('.media-gallery__grid > .product-media-container');
    const count = cells.length;

    gallery.classList.toggle('ep-gallery--single', count === 1);
    gallery.classList.toggle('ep-gallery--duo', count === 2);

    if (slideshow) slideshow.style.setProperty('display', 'none', 'important');

    if (grid) {
      grid.style.setProperty('display', 'grid', 'important');
      if (count > 1 && (isWide || isTablet)) {
        grid.style.setProperty('grid-template-columns', 'repeat(2, minmax(0, 1fr))', 'important');
      } else {
        grid.style.setProperty('grid-template-columns', '1fr', 'important');
      }
      grid.style.setProperty('gap', '12px', 'important');
    }

    cells.forEach((cell) => {
      cell.classList.remove('constrain-height');
      cell.style.setProperty('opacity', '1', 'important');
      cell.style.setProperty('visibility', 'visible', 'important');
      cell.style.setProperty(
        'grid-column',
        count === 1 || !isTablet ? '1 / -1' : 'span 1',
        'important'
      );
      cell.style.removeProperty('height');
    });

    const mediaCol = document.querySelector('.product-information__media');
    if (mediaCol) {
      mediaCol.style.setProperty('width', '100%', 'important');
      mediaCol.style.setProperty('min-width', '0', 'important');
      mediaCol.style.setProperty('max-width', '100%', 'important');
      mediaCol.style.setProperty('opacity', '1', 'important');
      mediaCol.style.setProperty('visibility', 'visible', 'important');
      mediaCol.style.setProperty('grid-column', isWide ? '1' : '1 / -1', 'important');
    }

    const gridEl = document.querySelector('.product-information__grid');
    if (gridEl) {
      gridEl.style.setProperty('display', 'grid', 'important');
      gridEl.style.setProperty(
        'grid-template-columns',
        isWide ? 'minmax(0, 1.05fr) minmax(0, 0.95fr)' : '1fr',
        'important'
      );
    }

    const details = document.querySelector('.product-details');
    if (details) {
      details.style.setProperty('grid-column', isWide ? '2' : '1 / -1', 'important');
      details.style.removeProperty('max-height');
      details.style.removeProperty('height');
      details.style.removeProperty('overflow-y');
      details.style.removeProperty('overflow');
      details.scrollTop = 0;
    }

    gallery.querySelectorAll('img.product-media__image, .media-gallery__grid img').forEach((img) => {
      img.style.setProperty('opacity', '1', 'important');
      img.style.setProperty('visibility', 'visible', 'important');
      img.classList.add('ef-img-ready');
      if (img.loading === 'lazy') img.loading = 'eager';
    });
  }

  /* ── Toasts sociaux (désactivés par défaut) ── */
  function initToasts() {
    if (document.body.dataset.toastEnabled !== 'true') return;
    if (isReduced()) return;

    const stack = document.getElementById('enfami-toast-stack');
    if (!stack) return;

    const names = ['Lucas', 'Emma', 'Noah', 'Léa', 'Mohamed', 'Chloé', 'Adam', 'Sarah'];
    const cities = ['Paris', 'Montréal', 'Lyon', 'Bruxelles', 'Marseille'];

    const show = () => {
      const toast = document.createElement('div');
      toast.className = 'enfami-toast';
      const name = names[Math.floor(Math.random() * names.length)];
      const city = cities[Math.floor(Math.random() * cities.length)];
      toast.innerHTML = `<strong>${name}</strong> de ${city} vient de commander un maillot`;
      stack.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('is-visible'));
      setTimeout(() => {
        toast.classList.remove('is-visible');
        setTimeout(() => toast.remove(), 500);
      }, 4500);
    };

    setTimeout(show, 3500);
    setInterval(show, 22000 + Math.random() * 10000);
  }

  /* ── Menu drawer stagger ── */
  function initMenuStagger() {
    const drawer = document.querySelector('.menu-drawer');
    if (!drawer) return;

    const items = drawer.querySelectorAll('.menu-drawer__menu-item--mainlist, .menu-drawer__animated-element');
    items.forEach((item, i) => {
      item.style.setProperty('--stagger-i', String(i));
      item.style.transitionDelay = `${i * 0.07}s`;
    });

    const details = document.querySelector('#Details-menu-drawer-container, .menu-drawer-container');
    if (!details) return;

    const observer = new MutationObserver(() => {
      const open = details.classList.contains('menu-open') || details.hasAttribute('open');
      drawer.classList.toggle('is-open', open);
      document.body.classList.toggle('menu-drawer-open', open);
      if (open) {
        items.forEach((item, i) => {
          item.style.opacity = '0';
          item.style.transform = 'translateY(20px)';
          requestAnimationFrame(() => {
            setTimeout(() => {
              item.style.opacity = '1';
              item.style.transform = 'none';
            }, i * 70);
          });
        });
      }
    });
    observer.observe(details, { attributes: true, attributeFilter: ['class', 'open'] });
  }

  /* ── Panier slide-in + pulse ajout ── */
  function initCartDrawerPremium() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;

    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.didError) return;
      drawer.classList.add('cart-drawer--pulse');
      setTimeout(() => drawer.classList.remove('cart-drawer--pulse'), 700);

      const icon = document.querySelector('.header-actions__cart-icon, cart-icon');
      icon?.classList.add('cart-icon--bounce');
      setTimeout(() => icon?.classList.remove('cart-icon--bounce'), 600);

      const firstItem = drawer.querySelector('.cart-items__item, .cart-drawer__items .cart-item');
      firstItem?.classList.add('cart-item--added');
      setTimeout(() => firstItem?.classList.remove('cart-item--added'), 900);
    });

    drawer.addEventListener('dialog:open', () => drawer.classList.add('cart-drawer--visible'));
    drawer.addEventListener('dialog:close', () => drawer.classList.remove('cart-drawer--visible'));
  }

  /* ── Boutons liquides (ripple) ── */
  function initLiquidButtons() {
    const sel = '.button:not(.quick-add__button), .enfami-btn-premium, .efc__upload-btn, .footer-enfami__btn, .product-form__submit';
    document.querySelectorAll(sel).forEach((btn) => {
      if (btn.dataset.liquidBound) return;
      if (btn.closest('quick-add-component, .quick-add')) return;
      btn.dataset.liquidBound = 'true';
      btn.classList.add('enfami-liquid-btn');
      btn.addEventListener('click', (e) => {
        if (isReduced()) return;
        const rect = btn.getBoundingClientRect();
        const ripple = document.createElement('span');
        ripple.className = 'enfami-liquid-btn__ripple';
        const size = Math.max(rect.width, rect.height) * 2;
        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = e.clientX - rect.left - size / 2 + 'px';
        ripple.style.top = e.clientY - rect.top - size / 2 + 'px';
        btn.appendChild(ripple);
        ripple.addEventListener('animationend', () => ripple.remove());
      });
    });
  }

  /* ── Liens underline auto ── */
  function initLinkUnderlines() {
    document.querySelectorAll('.menu-list__link, .footer-enfami__link, .header a:not(.button)').forEach((link) => {
      if (!link.classList.contains('enfami-link-underline')) {
        link.classList.add('enfami-link-underline');
      }
    });
  }

  /* ── Particules hero ── */
  function initHeroParticles() {
    if (isMobile() || isReduced()) return;
    const hero = document.querySelector('.enfami-premium-hero');
    if (!hero || hero.querySelector('.enfami-hero-particles')) return;

    const canvas = document.createElement('canvas');
    canvas.className = 'enfami-hero-particles';
    canvas.setAttribute('aria-hidden', 'true');
    hero.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;
    const particles = [];
    const COUNT = 42;

    function resize() {
      w = canvas.width = hero.offsetWidth;
      h = canvas.height = hero.offsetHeight;
    }

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random(),
        y: Math.random(),
        r: Math.random() * 1.8 + 0.4,
        vx: (Math.random() - 0.5) * 0.0004,
        vy: (Math.random() - 0.5) * 0.0004,
        a: Math.random() * 0.5 + 0.15,
      });
    }

    resize();
    window.addEventListener('resize', resize);

    function draw() {
      ctx.clearRect(0, 0, w, h);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200, 255, 0, ${p.a})`;
        ctx.fill();
      });
      requestAnimationFrame(draw);
    }
    draw();
  }

  /* ── Pin + scroll horizontal (marquee clients) ── */
  function initHorizontalPin() {
    if (isMobile() || isReduced()) return;
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return;

    document.querySelectorAll('[data-pin-hscroll]').forEach((section) => {
      const track = section.querySelector('[data-hscroll-track], .emc__track');
      if (!track || track.scrollWidth <= section.offsetWidth) return;

      section.classList.add('is-pin-active');
      const arrows = section.querySelectorAll('.emc__arrow');
      arrows.forEach((a) => (a.style.display = 'none'));

      gsap.to(track, {
        x: () => -(track.scrollWidth - section.offsetWidth + 48),
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => '+=' + Math.max(track.scrollWidth - section.offsetWidth + 48, 400),
          pin: true,
          scrub: 0.85,
          invalidateOnRefresh: true,
        },
      });
    });
  }

  /* ── Skeleton loaders collection ── */
  function initSkeletonLoaders() {
    const main = document.querySelector('#MainContent');
    if (main?.dataset.template !== 'collection' && main?.dataset.template !== 'search') return;

    document.querySelectorAll('product-card, .product-card').forEach((card) => {
      if (card.querySelector('.enfami-skeleton-wrap')) return;
      const gallery = card.querySelector('.product-card-gallery, [ref="cardGallery"]');
      if (!gallery) return;

      const wrap = document.createElement('div');
      wrap.className = 'enfami-skeleton-wrap';
      wrap.innerHTML = '<div class="enfami-skeleton enfami-skeleton--card"></div>';
      gallery.style.position = 'relative';
      gallery.appendChild(wrap);

      const img = gallery.querySelector('img');
      const hide = () => wrap.classList.add('is-hidden');
      if (img?.complete) hide();
      else img?.addEventListener('load', hide, { once: true });
      setTimeout(hide, 4000);
    });
  }

  /* ── Magnétique auto sur CTA principaux ── */
  function initAutoMagnetic() {
    document
      .querySelectorAll('.product-form__submit, .cart__checkout-button, .epb__cta')
      .forEach((btn) => {
        if (!btn.classList.contains('enfami-magnetic')) btn.classList.add('enfami-magnetic');
      });
  }

  /* ── View transition page fade ── */
  function initPageTransitionStyle() {
    if (isReduced()) return;
    document.addEventListener('pageswap', () => body.classList.add('page-is-transitioning'));
    document.addEventListener('pagereveal', () => {
      body.classList.remove('page-is-transitioning');
      body.classList.add('page-revealed');
      setTimeout(() => body.classList.remove('page-revealed'), 600);
    });
  }

  /* ── Animations modernes — stagger carousels, titres, sections ── */
  function initModernMotion(gsap, ScrollTrigger) {
    if (!gsap || !ScrollTrigger) return;

    gsap.utils.toArray('.enfami-carousel').forEach((section) => {
      const title = section.querySelector('.enfami-carousel__title');
      const eyebrow = section.querySelector('.enfami-carousel__eyebrow');
      const cards = section.querySelectorAll('.enfami-carousel__card');

      if (title) {
        gsap.fromTo(
          title,
          { opacity: 0, x: -48, skewX: 4 },
          {
            opacity: 1,
            x: 0,
            skewX: 0,
            duration: 0.85,
            ease: 'power3.out',
            scrollTrigger: { trigger: section, start: 'top 82%', toggleActions: 'play none none none' },
          }
        );
      }

      if (eyebrow) {
        gsap.fromTo(
          eyebrow,
          { opacity: 0, y: 16 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: 'power2.out',
            scrollTrigger: { trigger: section, start: 'top 85%', toggleActions: 'play none none none' },
          }
        );
      }

      if (cards.length) {
        gsap.fromTo(
          cards,
          { y: 48, scale: 0.96 },
          {
            y: 0,
            scale: 1,
            duration: 0.75,
            stagger: 0.07,
            ease: 'power3.out',
            immediateRender: false,
            scrollTrigger: { trigger: section, start: 'top 78%', toggleActions: 'play none none none' },
          }
        );
      }
    });

    gsap.utils.toArray('.efaq, .vc-section, .emc').forEach((section) => {
      const heading = section.querySelector('h2, .vc-title, .emc__title');
      if (!heading) return;
      gsap.fromTo(
        heading,
        { opacity: 0, y: 32 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: { trigger: section, start: 'top 85%', toggleActions: 'play none none none' },
        }
      );
    });

    gsap.utils.toArray('.efaq__item, .vc-card').forEach((el, i) => {
      gsap.fromTo(
        el,
        { opacity: 0, y: 28 },
        {
          opacity: 1,
          y: 0,
          duration: 0.65,
          delay: (i % 6) * 0.06,
          ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 92%', toggleActions: 'play none none none' },
        }
      );
    });
  }

  /* ── GSAP + Lenis + ScrollTrigger ── */
  function initScrollEngine() {
    if (isReduced()) {
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        el.classList.remove('is-pending-reveal');
        el.classList.add('is-revealed');
      });
      return;
    }

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const Lenis = window.Lenis;

    if (gsap && ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);
    }

    let lenis = null;

    if (Lenis && !isMobile() && !document.body.classList.contains('template-product')) {
      lenis = new Lenis({
        duration: 1.1,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        prevent: (node) => {
          if (!node || !node.closest) return false;
          return Boolean(
            node.closest('[data-lenis-prevent]') ||
              node.closest('dialog[open]') ||
              node.closest('.cart-drawer__dialog') ||
              node.closest('.cart-drawer__items') ||
              node.closest('.cart-drawer__content')
          );
        },
      });
      window.__enfamiLenis = lenis;

      const syncLenisWithDrawer = () => {
        const openDrawer = document.querySelector('cart-drawer-component dialog[open], .cart-drawer__dialog[open]');
        if (openDrawer) lenis.stop();
        else lenis.start();
      };

      document.addEventListener('toggle', syncLenisWithDrawer, true);
      document.addEventListener('click', () => requestAnimationFrame(syncLenisWithDrawer), true);
      const drawerDialog = document.querySelector('.cart-drawer__dialog');
      if (drawerDialog) {
        drawerDialog.addEventListener('close', () => lenis.start());
        drawerDialog.addEventListener('cancel', () => lenis.start());
      }
      new MutationObserver(syncLenisWithDrawer).observe(document.documentElement, {
        attributes: true,
        subtree: true,
        attributeFilter: ['open'],
      });

      lenis.on('scroll', ({ scroll }) => {
        document.dispatchEvent(new CustomEvent('enfami:scroll', { detail: { scroll } }));
      });

      if (ScrollTrigger) {
        ScrollTrigger.scrollerProxy(document.documentElement, {
          scrollTop(value) {
            if (arguments.length) {
              lenis.scrollTo(value, { immediate: true });
            }
            return lenis.scroll;
          },
          getBoundingClientRect() {
            return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
          },
        });

        ScrollTrigger.addEventListener('refresh', () => lenis.resize());
      }

      gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
      });
      gsap.ticker.lagSmoothing(0);
    } else if (ScrollTrigger) {
      window.addEventListener('scroll', () => {
        document.dispatchEvent(new CustomEvent('enfami:scroll', { detail: { scroll: window.scrollY } }));
        ScrollTrigger.update();
      }, { passive: true });
    }

    /* Reveal — marquer pending, animer avec GSAP (sauf PDP : visible immédiatement) */
    document.querySelectorAll('[data-reveal]').forEach((el) => {
      if (
        el.closest('.product-information') ||
        (document.body.classList.contains('template-product') && el.querySelector('.product-information'))
      ) {
        el.classList.remove('is-pending-reveal');
        el.classList.add('is-revealed');
        return;
      }
      el.classList.add('is-pending-reveal');
    });

    if (gsap && ScrollTrigger) {
      gsap.utils.toArray('[data-reveal].is-pending-reveal').forEach((el) => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.9,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 88%',
              toggleActions: 'play none none none',
            },
            onComplete: () => {
              el.classList.remove('is-pending-reveal');
              el.classList.add('is-revealed');
            },
          }
        );
      });

      /* Parallax */
      gsap.utils.toArray('[data-parallax]').forEach((el) => {
        const speed = parseFloat(el.getAttribute('data-parallax') || '0.3');
        gsap.to(el, {
          yPercent: speed * 100,
          ease: 'none',
          scrollTrigger: {
            trigger: el.closest('section') || el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      });

      /* Pinning */
      document.querySelectorAll('[data-pin]').forEach((section) => {
        const inner = section.querySelector('[data-pin-inner]');
        if (!inner) return;
        ScrollTrigger.create({
          trigger: section,
          start: 'top top',
          end: () => '+=' + (inner.scrollHeight || 600),
          pin: true,
          pinSpacing: true,
          anticipatePin: 1,
        });
      });

      initHorizontalPin();
      initModernMotion(gsap, ScrollTrigger);

      ScrollTrigger.refresh();
    } else {
      /* Fallback Intersection Observer */
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );
      document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
    }

    document.dispatchEvent(new CustomEvent('enfami:premium-ready'));

    return lenis;
  }

  /* ── Boot ── */
  function bootScrollWithRetry(attempt) {
    if (window.gsap && window.ScrollTrigger) {
      initScrollEngine();
      return;
    }
    if (attempt > 30) {
      initScrollEngine();
      return;
    }
    setTimeout(() => bootScrollWithRetry(attempt + 1), 50);
  }

  function boot() {
    initLoader();
    initScrollProgress();
    initHeaderGlass();
    initCursor();
    initMagnetic();
    initTilt();
    initCounters();
    initProductGalleryFix();
    setTimeout(initProductGalleryFix, 200);
    setTimeout(initProductGalleryFix, 1000);
    window.addEventListener('resize', initProductGalleryFix);
    initToasts();
    initMenuStagger();
    initCartDrawerPremium();
    initLiquidButtons();
    initLinkUnderlines();
    initAutoMagnetic();
    initPageTransitionStyle();
    bootScrollWithRetry(0);
    initSkeletonLoaders();
  }

  document.addEventListener('enfami:premium-ready', () => {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:load', () => {
    initMagnetic();
    initTilt();
    initLiquidButtons();
    initLinkUnderlines();
    initSkeletonLoaders();
    initProductGalleryFix();
    if (window.ScrollTrigger) {
      initHorizontalPin();
      window.ScrollTrigger.refresh();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
